import { useCallback, useEffect, useState } from "react";

import { useLazyQuery, useQuery } from "@apollo/client";
import uniqBy from 'lodash/uniqBy';

import ENV from "../../../app/constants/env";
import getObjectCommand from "../../../app/constants/utils";
import { FETCH_HOMEPAGE_CONFIG_AND_VERSION, FETCH_RATINGS_SUMMARY_LAST_60_DAYS, FETCH_RECIPES_BY_CATEGORY_AND_INGREDIENTS, GET_ALL_DATA, GET_FAVOURITE_RECIPES } from "../../../app/graphql/queries";
import { HomeConfigSelector, HomeConfigVersionSelector, setHomePageData, setUserFavorites } from "../../../app/homepage/homepage.slice";
import { getAllRecipesDataHandler } from "../../../app/homepage/homepage.thunkActions";
import { CategoryRecipeID, IngredientsQueryInterface } from "../../../app/homepage/homepage.types";
import { allergenFilter } from "../../../app/homepage/homepage.utils";
import { userIdSelector, userSelector } from "../../../app/main/mainSlice";
import { RatingsInterface, SearchFilteredRecipesInterface } from "../../../app/search/search.types";
import { useAppDispatch, useAppSelector } from "../../../app/store/hooks";
import useFetchGroceryList from "../../../hooks/useFetchGroceryList";
import { recipeMetaDataTransformerFS2QL } from "../../../utils/RecipeBackEnd/hasuraFSMigrationTransformers";
import { COMPONENTS } from "./constants";
import { HomeScreen, ProcessRecipesData } from "./homepageJson.types";
import { getDietArray, isJsonString, jsonSanitizer } from "./newHomePageUtils";

const {
  HOMEPAGE_BUCKET_NAME,
  DEFAULT_HOME_JSON,
} = ENV;

function processRecipesNew(
  data: ProcessRecipesData,
  userAllergens: string[],
  ratingsData: RatingsInterface[]
): {
  categoryToRecipeId: CategoryRecipeID,
  ingredients: IngredientsQueryInterface[],
  transformedRecipes: SearchFilteredRecipesInterface[]
} {
  const categoryToRecipeMap = new Map<number, { categoryName: string, recipeIds: string[] }>();
  const transformedRecipes: any[] = [];
  const ingredients: IngredientsQueryInterface[] = Array.isArray(data?.Ingredients_DB) ? data.Ingredients_DB : [];
  const uniqueRecipeIds = new Set<string>();

  if (!Array.isArray(data?.recipe_categories)) {
    console.error("a: No valid recipe categories found in the data.");
    return { categoryToRecipeId: {}, ingredients, transformedRecipes };
  }
  const ratingsObject: Record<string, { rating: number | null, ratingCount: number | null }> = {};
  ratingsData.forEach((rating: RatingsInterface) => {
    ratingsObject[rating.recipeID] = {
      rating: rating.rating || null,
      ratingCount: rating.ratingCount || null,
    };
  });

  data.recipe_categories.forEach((category: any) => {
    if (!Array.isArray(category?.Recipe_Categories_Map)) {
      console.warn(`a: No valid Recipe_Categories_Map found for category ID ${category.id}`);
      return;
    }

    const sortedRecipesMap = uniqBy(
      [...category.Recipe_Categories_Map].sort((a: any, b: any) => a.map_rank - b.map_rank),
      (map: any) => map.Recipes_E3?.id,
    );
    sortedRecipesMap.forEach((map: any) => {
      const recipe = map?.Recipes_E3;
      if (!recipe) {
        console.warn(`a: No Recipes_E3 found for map in category ID ${category.id}`);
        return;
      }

      try {
        if (!uniqueRecipeIds.has(recipe.id)) {
          const { rating, ratingCount } = ratingsObject[recipe.id] || { rating: null, ratingCount: null };
          transformedRecipes.push({
            ...recipe,
            rating,
            ratingCount,
          });

          uniqueRecipeIds.add(recipe.id);
        }

        if (!categoryToRecipeMap.has(category.id)) {
          categoryToRecipeMap.set(category.id, {
            categoryName: map.recipe_category?.name || '',
            recipeIds: [],
          });
        }
        categoryToRecipeMap.get(category.id)?.recipeIds.push(recipe.id);
      } catch (error) {
        console.error(`a: Error transforming recipe ID ${recipe.id}:`, error);
      }
    });
  });

  const transformedRecipesWithAllergens = recipeMetaDataTransformerFS2QL(transformedRecipes, ingredients);
  const filteredRecipes = transformedRecipesWithAllergens.filter((recipe: SearchFilteredRecipesInterface) => {
    const recipeAllergens = recipe.allergens;
    return !allergenFilter(userAllergens, recipeAllergens);
  });

  const categoryToRecipeId: CategoryRecipeID = {};

  categoryToRecipeMap.forEach((value, categoryId) => {
    categoryToRecipeId[categoryId] = {
      categoryName: value.categoryName,
      recipeIds: Array.from(value.recipeIds).filter(recipeId =>
        filteredRecipes.some((recipe: SearchFilteredRecipesInterface) => recipe.id === recipeId)
      ),
    };
  });

  return { categoryToRecipeId, ingredients, transformedRecipes: filteredRecipes };
}


export function getCategoryIds(screenObject: HomeScreen): string[] {
  const categoryIds: string[] = [];

  if (screenObject?.contents && screenObject?.contents?.length > 0) {
    screenObject?.contents?.forEach((content: any) => {
      if (content?.component === COMPONENTS.CATEGORY && content?.category_id) {
        categoryIds.push(content?.category_id);
      }
    });
  }
  return categoryIds;
}

export const useGetAllLazyQuery = () => {
  const user = useAppSelector((state) => state.main.user);
  const dispatch = useAppDispatch();
  const { loading } = useQuery(GET_ALL_DATA, {
    skip: !user?.id,
    variables: {
      userId: user?.id,
      userIdRecipes: user?.id
    },
    onCompleted: (data) => {
      dispatch(getAllRecipesDataHandler(data));
    },
    onError: (e) => {
      console.error('Error fetching data:', e);
    },
  });

  return { loading };
}

const useHomePageJson = (loadLazy?: boolean) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(!loadLazy);
  const user = useAppSelector(userSelector);
  const userId = useAppSelector(userIdSelector);
  const homeVersion = useAppSelector(HomeConfigVersionSelector);
  const homeConfig = useAppSelector(HomeConfigSelector);

  const diet = user?.userSettings?.diet;
  const allergens = user?.userSettings?.allergens;

  const [fetchHomePageJson] = useLazyQuery(FETCH_HOMEPAGE_CONFIG_AND_VERSION);
  const [fetchRecipesByCategoryAndIngredients] = useLazyQuery(FETCH_RECIPES_BY_CATEGORY_AND_INGREDIENTS);
  const [fetchRatingsSummary] = useLazyQuery(FETCH_RATINGS_SUMMARY_LAST_60_DAYS);
  const [fetchUserFavouriteRecipes] = useLazyQuery(GET_FAVOURITE_RECIPES);
  const { loading: groceryListLoading } = useFetchGroceryList();
  useGetAllLazyQuery();

  const fetchHomePage = useCallback(async (newDiet?: string | null, newAllergens?: string[]) => {
    setLoading(true);
    let newHomeConfig = homeConfig;
    const userDiet: string[] = newDiet ? getDietArray(newDiet) : getDietArray(diet);
    const userAllergens = newAllergens || allergens || [];

    const dbJson = await fetchHomePageJson({
      variables: { userId },
    });
    const fetchedVersion = dbJson?.data?.User_Home_Config?.[0]?.mobile_version ?? 0;
    const fetchedJson = dbJson?.data?.User_Home_Config?.[0]?.mobile_config;
    const defaultJson = homeVersion === fetchedVersion;
    if (!defaultJson && fetchedJson) newHomeConfig = fetchedJson;

    else if (!fetchedJson) {
      const key = DEFAULT_HOME_JSON;
      const homeJson = await getObjectCommand(HOMEPAGE_BUCKET_NAME, key);
      const sanitizedData = isJsonString(homeJson) ? JSON.parse(homeJson) : JSON.parse(JSON.stringify(homeJson));
      newHomeConfig = sanitizedData;
    }

    const categoryIds = getCategoryIds(newHomeConfig);

    if (categoryIds.length > 0) {
      const fetchedRecipeAndIngredients = await fetchRecipesByCategoryAndIngredients({ variables: { categoryIds, diet: userDiet } });

      const fetchedRatings = await fetchRatingsSummary();

      const fetchedUserFavouriteRecipes = await fetchUserFavouriteRecipes({ variables: { userId } });

      if (fetchedUserFavouriteRecipes?.data?.User_favourite_recipes?.length > 0) {
        const favRecipeIds: string[] = fetchedUserFavouriteRecipes.data.User_favourite_recipes.map(
          (recipe: { recipeId: string }) => recipe.recipeId
        );
        dispatch(setUserFavorites(favRecipeIds));
      }

      const { categoryToRecipeId, transformedRecipes: recipes, ingredients } = processRecipesNew(
        fetchedRecipeAndIngredients.data,
        userAllergens,
        fetchedRatings?.data?.ratingsSummaryLast60DaysView || [],
      );

      if (categoryToRecipeId && recipes) {
        newHomeConfig = jsonSanitizer(newHomeConfig, categoryToRecipeId);
        localStorage.setItem('mobile_version', fetchedVersion);
        localStorage.setItem('mobile_config', JSON.stringify(newHomeConfig));
        localStorage.setItem('filteredCategoryToRecipeData', JSON.stringify(categoryToRecipeId));
        localStorage.setItem('recipeList', JSON.stringify(recipes ?? []));
        dispatch(
          setHomePageData({
            mobile_config: newHomeConfig,
            mobile_version: fetchedVersion,
            recipes,
            ingredients,
            categoryToRecipeId,
          }),
        );
      }
    }
    setLoading(false);

  }, [diet, allergens]);


  useEffect(() => {
    if (!loadLazy) {
      fetchHomePage();
    }
  }, [loadLazy]);

  const finalLoading = loading || groceryListLoading;
  return { loading: finalLoading, homeConfig, fetchHomePage };
};

export default useHomePageJson;